import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Scale, Eye } from "lucide-react";
import Header from "../components/Header";
import PageHero from "../components/PageHero";
import Footer from "../components/Footer";
import { useI18n } from "../lib/i18n";

export default function About() {
  const { t } = useI18n();

  const values = [
    {
      icon: Scale,
      title: t("fair"),
      desc: "No bias. No shortcuts. Neutral arbitration, evidence-based decisions.",
    },
    {
      icon: Zap,
      title: t("fast"),
      desc: "Digital from start to finish. Agreement signed in minutes. ORE-AI delivers instant dispute verdicts.",
    },
    {
      icon: Eye,
      title: t("transparent"),
      desc: "No hidden fees. No surprise deductions. Every step tracked and timestamped.",
    },
  ];

  return (
    <div style={{ background: "#080808", color: "#F5F5F5", minHeight: "100vh" }}>
      <Header />

      {/* Hero Section */}
      <PageHero
        eyebrow={t("nav_about")}
        title={t("renting_broken")}
        subtitle="We're fixing the Indian rental market, one deposit at a time."
        cta1Text="Get Started"
        cta1Link="/register"
        hasGlow={true}
        gradientWord="broken"
      />

      {/* Our Story */}
      <section style={{ background: "#0B0B14", padding: "4rem 0" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 mb-6">
            <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
            <p
              className="text-xs tracking-widest uppercase"
              style={{
                background: "linear-gradient(135deg, #F97316, #A78BFA)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              The Beginning
            </p>
          </div>
          <h2
            className="font-display font-bold mb-12"
            style={{
              fontSize: "clamp(2rem, 4vw, 3.5rem)",
              color: "#F5F5F5",
            }}
          >
            We started because we got angry.
          </h2>

          <div className="space-y-6 text-lg font-light leading-relaxed" style={{ color: "rgba(245,245,245,0.6)" }}>
            <p>
              In 2023, one of us paid a ₹1.5 lakh deposit to move into a shared apartment in Bangalore. Three months later, they got back ₹20,000. No itemised list. No photos of the "damage". No explanation. Just a message on WhatsApp saying the rest was for cleaning and repairs.
            </p>
            <p>
              So we did what any angry renter would do: we tried to get it back. We wrote emails. We called the landlord. We found out there was no legal recourse because there was no agreement. Just WhatsApp and cash-in-hand tradition.
            </p>
            <p>
              We started talking to brokers, landlords, and other tenants. Same story everywhere. Deposits vanished. Conditions changed. Nobody had proof. Brokers got blamed for conflicts they couldn't solve. Landlords lost money to claims they couldn't dispute. Tenants lost their life savings to what amounted to a handshake deal.
            </p>
            <p>
              The Indian rental market is broken. Not because of bad people — but because it's running on paper, WhatsApp, and trust that breaks down the moment money moves.
            </p>
            <p>
              So we built Orello. A platform where deposits are held securely — not in the landlord's account. Agreements are digital, e-stamped, and court-valid. Property conditions are documented room-by-room with timestamped photos. And if there's ever a dispute, ORE-AI — India's first purpose-built rental dispute engine — reviews the evidence and gives an instant fair verdict. No arguments. No lawyers. No drama.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section style={{ background: "#111111", padding: "4rem 0" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="flex items-center gap-2 mb-6 justify-center">
              <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
              <p
                className="text-xs tracking-widest uppercase"
                style={{
                  background: "linear-gradient(135deg, #F97316, #A78BFA)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Our Values
              </p>
            </div>
            <h2
              className="font-display font-bold"
              style={{
                fontSize: "clamp(2rem, 4vw, 3.5rem)",
                color: "#F5F5F5",
              }}
            >
              Fair. Fast. Transparent.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, i) => (
              <div
                key={i}
                style={{
                  padding: "2.5rem",
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    background: "rgba(249,115,22,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "1.5rem",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                >
                  <value.icon size={32} style={{ color: "#F97316" }} />
                </div>
                <h3 className="font-display font-bold mb-3" style={{ fontSize: "1.5rem", color: "#F5F5F5" }}>
                  {value.title}
                </h3>
                <p style={{ color: "rgba(245,245,245,0.5)", lineHeight: 1.6 }}>
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section style={{ background: "#0B0B14", padding: "4rem 0" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="flex items-center gap-2 mb-6 justify-center">
              <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
              <p
                className="text-xs tracking-widest uppercase"
                style={{
                  background: "linear-gradient(135deg, #F97316, #A78BFA)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Our Team
              </p>
            </div>
            <h2
              className="font-display font-bold"
              style={{
                fontSize: "clamp(2rem, 4vw, 3.5rem)",
                color: "#F5F5F5",
              }}
            >
              Built by people who rent.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Anjali Sharma",
                role: "Co-founder & CEO",
                bio: "Former fintech PM who lost her deposit and got angry.",
              },
              {
                name: "Rohan Patel",
                role: "Co-founder & CTO",
                bio: "Engineer who lived through 5 rentals and 4 deposit disputes.",
              },
              {
                name: "Priya Menon",
                role: "Head of Legal",
                bio: "Rental law expert who saw the system's gaps and wanted to fix them.",
              },
            ].map((member, i) => (
              <div
                key={i}
                style={{
                  padding: "2rem",
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(249,115,22,0.2), rgba(139,92,246,0.2))",
                    border: "2px solid rgba(249,115,22,0.4)",
                    marginBottom: "1.5rem",
                    marginLeft: "auto",
                    marginRight: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#F97316",
                    fontWeight: 700,
                    fontSize: "1.5rem",
                  }}
                >
                  {member.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <h3 className="font-display font-bold mb-1" style={{ fontSize: "1.15rem", color: "#F5F5F5" }}>
                  {member.name}
                </h3>
                <p style={{ fontSize: "0.9rem", color: "#F97316", marginBottom: "1rem" }}>
                  {member.role}
                </p>
                <p style={{ fontSize: "0.9rem", color: "rgba(245,245,245,0.5)", lineHeight: 1.6 }}>
                  {member.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ background: "#080808", padding: "4rem 0" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2
            className="font-display font-bold leading-tight mb-6"
            style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", color: "#F5F5F5" }}
          >
            Help us fix Indian rentals.<br />
            <span
              style={{
                background: "linear-gradient(135deg, #F97316, #EC4899)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              start renting with Orello.
            </span>
          </h2>
          <p className="text-base font-light mb-12" style={{ color: "rgba(245,245,245,0.35)" }}>
            Be part of the rental revolution.
          </p>
          <Link
            to="/register"
            className="btn-press inline-flex items-center gap-2 font-medium px-8 py-4 rounded-full text-white"
            style={{
              background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
              fontSize: "1.05rem",
              boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
            }}
          >
            Create Free Account <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
